import { useStore } from "~/store";
import { useSeoUrls, textoPlano } from "./useSeoUrls";

const DIAS_SCHEMA = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const HORA = /^([01]\d|2[0-3]):[0-5]\d$/

/** Horario estructurado (tramos con días y horas) → openingHoursSpecification. Sin tramos válidos no se publica nada. */
function horarioSchema(tramos: any): any[] | undefined {
  const spec = (Array.isArray(tramos) ? tramos : [])
    .map((t: any) => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": (t?.Dias ?? []).filter((d: string) => DIAS_SCHEMA.includes(d)),
      "opens": t?.Abre,
      "closes": t?.Cierra
    }))
    .filter((t: any) => t.dayOfWeek.length && HORA.test(t.opens ?? '') && HORA.test(t.closes ?? ''))
  return spec.length ? spec : undefined
}

/** Lo que cambia de una página a otra en los datos estructurados. */
export interface SchemaPagina {
  /** URL canónica de la página. */
  url: string
  esPortada: boolean
  /** Migas de pan, de la portada a la página actual. */
  migas: { name: string; url: string }[]
  /** Tipo de contenido de la página: un servicio o un artículo del blog. */
  tipo?: 'Service' | 'BlogPosting'
  titulo?: string
  descripcion?: string
  imagen?: string
  /** Fechas en formato AAAA-MM-DD. */
  publicado?: string
  modificado?: string
  autor?: string
  /** Preguntas y respuestas visibles en la página. */
  faqs?: { p: string; r: string }[]
}

export function useSchemaOrg(notaria: any, pagina?: SchemaPagina) {
  const store = useStore();
  const seo = useSeoUrls();
  
  if (!notaria || !notaria.Contacto) return;

  const idNotaria = `${seo.origen}/#notaria`;
  const idWeb = `${seo.origen}/#web`;

  // Enhance AEO (Answer Engine Optimization) by exposing all services offered
  const offeredServices = [];
  if (notaria.Servicios && Array.isArray(notaria.Servicios)) {
    notaria.Servicios.filter((s: any) => s.Publicado).forEach((s: any) => {
      offeredServices.push({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": s.Titulo,
          "description": s.Subtitulo || s.Resumen || s.Titulo,
          "url": seo.url(seo.idioma, `/servicios/${s.Slug}`)
        }
      });
    });
  }

  // Extract Team Members for AEO
  const teamMembers = [];
  if (notaria.Team && Array.isArray(notaria.Team)) {
    notaria.Team.forEach((t: any) => {
      if (t.Nombre) {
        teamMembers.push({
          "@type": "Person",
          "name": t.Nombre,
          "jobTitle": t.Puesto || undefined,
          "email": t.Email || undefined
        });
      }
    });
  }

  // Extract Principal Notaries
  if (notaria.Notarios && Array.isArray(notaria.Notarios)) {
    notaria.Notarios.forEach((n: any) => {
      if (n.Nombre) {
        teamMembers.push({
          "@type": "Person",
          "name": n.Nombre,
          "jobTitle": "Notario",
          "email": n.Email || undefined,
          "url": n.Web || undefined
        });
      }
    });
  }

  // minotaria.es no es una notaría: no se le pone dirección, notarios ni servicios de ninguna
  const agregador = String(notaria.Web?.Plantilla || '').toLowerCase() === 'minotaria'
    || String(notaria.Web?.Tipo || '').toLowerCase() === 'agregador';
  const coordenadas = notaria.Contacto.Coordinates;
  const negocio: any = agregador ? {
    "@type": "Organization",
    "@id": idNotaria,
    "name": notaria.Nombre,
    "url": seo.url(seo.principal, '/'),
    "logo": notaria.Theme?.Home?.Banner?.ImagenLogo ? `${seo.origen}/appdata/static/${notaria.Theme.Home.Banner.ImagenLogo}` : undefined,
    "sameAs": [notaria.Contacto.Facebook, notaria.Contacto.LinkedIn, notaria.Contacto.Twitter, notaria.Contacto.Instagram].filter(Boolean)
  } : {
    "@type": ["Notary", "LegalService", "LocalBusiness"],
    "@id": idNotaria,
    "name": notaria.Nombre,
    "description": notaria.Theme?.Home?.Description || notaria.Theme?.Home?.Titulo?.Texto || `Servicios notariales en ${notaria.Contacto.Municipio}`,
    "url": seo.url(seo.principal, '/'),
    "logo": notaria.Theme?.Home?.Banner?.ImagenLogo ? `${seo.origen}/appdata/static/${notaria.Theme.Home.Banner.ImagenLogo}` : undefined,
    "image": notaria.Theme?.Home?.Banner?.Imagen ? `${seo.origen}/appdata/static/${notaria.Theme.Home.Banner.Imagen}` : undefined,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": notaria.Contacto.Direccion,
      "addressLocality": notaria.Contacto.Municipio,
      "addressRegion": notaria.Contacto.Provincia,
      "postalCode": notaria.Contacto.CP,
      "addressCountry": "ES"
    },
    "geo": coordenadas?.lat && coordenadas?.lng ? {
      "@type": "GeoCoordinates",
      "latitude": coordenadas.lat,
      "longitude": coordenadas.lng
    } : undefined,
    "areaServed": notaria.Contacto.Municipio ? { "@type": "City", "name": notaria.Contacto.Municipio } : undefined,
    "knowsLanguage": seo.idiomas,
    "telephone": notaria.Contacto.Telefono || notaria.Contacto.Movil,
    "openingHoursSpecification": horarioSchema(notaria.Contacto.HorarioEstructurado),
    "email": notaria.Contacto.Email,
    "hasMap": notaria.Contacto.EnlaceMaps || undefined,
    "sameAs": [
      notaria.Contacto.Facebook,
      notaria.Contacto.LinkedIn,
      notaria.Contacto.Twitter,
      notaria.Contacto.Instagram
    ].filter(Boolean)
  };

  if (!agregador && offeredServices.length > 0) {
    negocio.makesOffer = offeredServices;
  }

  if (!agregador && teamMembers.length > 0) {
    negocio.employee = teamMembers;
  }

  const grafo: any[] = [
    negocio,
    {
      "@type": "WebSite",
      "@id": idWeb,
      "url": seo.url(seo.principal, '/'),
      "name": notaria.Nombre,
      "inLanguage": seo.idiomas,
      "publisher": { "@id": idNotaria }
    }
  ];

  if (pagina) {
    // Migas de pan (no tiene sentido en la portada, que es la raíz)
    if (pagina.migas.length > 1) {
      grafo.push({
        "@type": "BreadcrumbList",
        "itemListElement": pagina.migas.map((m, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": m.name,
          "item": m.url
        }))
      });
    }

    // Preguntas frecuentes: solo en la portada, que es donde se ven
    const faqs = Array.isArray(notaria.FAQs) ? notaria.FAQs.filter((f: any) => f.Pregunta && f.Respuesta) : [];
    if (pagina.esPortada && notaria.Theme?.Home?.FAQ?.Mostrar && faqs.length > 0) {
      grafo.push({
        "@type": "FAQPage",
        "mainEntity": faqs.map((f: any) => ({
          "@type": "Question",
          "name": textoPlano(f.Pregunta),
          "acceptedAnswer": { "@type": "Answer", "text": textoPlano(f.Respuesta) }
        }))
      });
    }

    // Preguntas frecuentes propias de la página (un trámite)
    if (pagina.faqs?.length) {
      grafo.push({
        "@type": "FAQPage",
        "mainEntity": pagina.faqs.map((f) => ({
          "@type": "Question",
          "name": f.p,
          "acceptedAnswer": { "@type": "Answer", "text": f.r }
        }))
      });
    }

    if (pagina.tipo === 'Service') {
      grafo.push({
        "@type": "Service",
        "@id": `${pagina.url}#servicio`,
        "name": pagina.titulo,
        "description": pagina.descripcion || undefined,
        "url": pagina.url,
        "image": pagina.imagen || undefined,
        "serviceType": pagina.titulo,
        "provider": { "@id": idNotaria },
        "areaServed": !agregador && notaria.Contacto.Municipio ? { "@type": "City", "name": notaria.Contacto.Municipio } : undefined,
        "inLanguage": seo.idioma
      });
    } else if (pagina.tipo === 'BlogPosting') {
      grafo.push({
        "@type": "BlogPosting",
        "@id": `${pagina.url}#articulo`,
        "headline": pagina.titulo,
        "description": pagina.descripcion || undefined,
        "image": pagina.imagen || undefined,
        "datePublished": pagina.publicado || undefined,
        "dateModified": pagina.modificado || pagina.publicado || undefined,
        "author": pagina.autor ? { "@type": "Person", "name": pagina.autor } : { "@id": idNotaria },
        "publisher": { "@id": idNotaria },
        "mainEntityOfPage": pagina.url,
        "inLanguage": seo.idioma
      });
    }
  }

  useHead({
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({ "@context": "https://schema.org", "@graph": grafo })
      }
    ]
  });
}
